import { yupResolver } from '@hookform/resolvers';
import { Box, Button, Dialog, DialogContent, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TuneIcon from '@material-ui/icons/Tune';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import LoadingContainer from '../../components/LoadingContainer/index';
import { path } from '../../core/api/search';
import { httpClient } from '../../core/config/httpClient';
import { FilterQuerySaveRequest } from '../../generated/model/models';
import {
  useFilterDispatch,
  useFilterState,
} from '../../pages/FinancialData/shared/FinancialDataContext/FinancialDataContext';
import {
  FinancialDataContextActionTypes,
  RequestParams,
} from '../../pages/FinancialData/shared/FinancialDataContext/FinancialDataContext.types';
import InputCheckbox from '../Inputs/Checkbox';
import InputField from '../Inputs/TextField';
import DialogTitleBlock from './Dialog/DialogTitleBlock';

interface OwnProps {
  show: boolean;
  handleClose: () => void;
}

export interface FilterSavingRequest {
  filters: any;
  formData: any;
  historySearch: boolean;
}

const FilterSavingModal: React.FC<OwnProps> = ({ show, handleClose }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorSaveFilter, setErrorSaveFilter] = useState('');
  const [maxCountSaveFilter, setMaxCountSaveFilter] = useState(0);
  const { data, historySearch, sortModel } = useFilterState();
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);
  const { register, handleSubmit, errors } = useForm({
    resolver: yupResolver(SignupSchema),
  });
  const dispatchFilter = useFilterDispatch();

  useEffect(() => {
    if (show !== open) {
      setOpen(!open);
      setIsLoading(false);
      setErrorSaveFilter('');
    }
  }, [show, open, setOpen]);

  const onSubmit = async (formData: any) => {
    setIsLoading(true);
    setDisabled(true);
    const requestData: FilterQuerySaveRequest = {};

    requestData.searchRequest = { filters: data, historySearch };
    requestData.name = formData.name;
    requestData.comments = formData.comments;
    requestData.generateReports = formData.searchResults;

    const params: RequestParams = {};

    if (sortModel?.length) {
      params.sort = sortModel.map((sortProperty) => `${sortProperty.colId},${sortProperty.sort}`);
    }

    try {
      const reponse = await httpClient.post<FilterQuerySaveRequest>(path.saveFilter, requestData, {
        params,
      });
      if (reponse) {
        dispatchFilter({
          type: FinancialDataContextActionTypes.SetSaveFilterButtonState,
          payload: true,
        });
        setOpen(false);
        handleClose();
      }
    } catch (err) {
      const errorText = err.statusText !== '' ? err.statusText : err.data.error;
      setErrorSaveFilter(errorText);
      setMaxCountSaveFilter(err.data.detail);
    } finally {
      setIsLoading(false);
      setDisabled(false);
      dispatchFilter({
        type: FinancialDataContextActionTypes.SetSaveFilterButtonState,
        payload: false,
      });
    }
  };

  return (
    <Box>
      <Dialog
        disableRestoreFocus={true}
        onClose={() => {
          setOpen(false);
          handleClose();
        }}
        open={open}
        className={classes.dialogBox}
      >
        <DialogTitleBlock
          icon={<TuneIcon />}
          title={t('companies:saveFilters')}
          handleClose={handleClose}
          disabled={disabled}
        />
        <DialogContent>
          {isLoading ? (
            <LoadingContainer />
          ) : errorSaveFilter ? (
            <Typography color="primary">
              <Trans
                i18nKey={`error:${errorSaveFilter}`}
                count={maxCountSaveFilter}
                defaults={
                  'Something went wrong. Feel free to notify us via Livechat if' +
                  ' this error occurs again.'
                }
              />
            </Typography>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <InputField
                label={t('companies:name')}
                name="name"
                mandatory={true}
                refReg={register}
                errors={errors}
                focus
              />
              <InputField
                label={t('companies:comments')}
                name="comments"
                multiline={true}
                errors={errors}
                refReg={register}
              />
              <InputCheckbox
                label={t('companies:saveSearchResults')}
                name="searchResults"
                defaultValue={false}
                refReg={register}
              />
              <Button type="submit" color="primary" variant="contained" disabled={disabled}>
                {t('app:save')}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const SignupSchema: any = yup.object().shape({
  name: yup.string().required('companies:requiredField'),
});

const useStyles = makeStyles((theme) => ({
  input: {
    padding: theme.spacing(0),
  },
  dialogBox: {
    '& .MuiDialogContent-root .MuiPaper-root': {
      borderRadius: 0,
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    color: theme.palette.grey[500],
  },
}));

export default FilterSavingModal;
